import {
  AppAdminFormLabel,
  AppBreadcrumb,
  AppButton,
  AppContainer,
  AppModal,
  AppPagination,
} from '@/components/atoms';
import { Plus, Search } from '@/components/atoms/AppIcon';
import RezeptLimitTable from '@/components/molecules/RezeptLimitTable';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import RezeptLimitForm from '@/components/organisms/RezeptLimitForm';
import { useAdminTheme } from '@/shared/providers/admin-theme/AdminThemeContext';
import type { RezeptLimit, RezeptLimitFormData } from '@/types/admin-rezept-limit';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import type { SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';
import { RA2NDatePicker } from '../organisms';
import { convertWareki, convertSeirekiWrap } from '@/utils/wareki';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';

const yearPickerFormat = parseFormat(JA_JP.formatYear);

const getYearString = (value: string): string => {
  if (!value.trim()) return '';
  const d = parseDate(value.trim(), yearPickerFormat);
  if (Number.isNaN(d.getTime())) return value;
  return String(d.getFullYear());
};

interface AdminRezeptLimitListTemplateProps {
  data: RezeptLimit[];
  current: number;
  total: number;
  pageSize: number;
  sortInfo?: { columnKey?: string; order?: 'ascend' | 'descend' };
  isLoading?: boolean;
  onChange: (page: number) => void;
  onTableChange?: (sorter: SorterResult<RezeptLimit> | SorterResult<RezeptLimit>[]) => void;
  breadcrumbItems: BreadcrumbItemType[];
  searchYear?: string;
  onSearchYear?: (value: string) => void;
  onSearch?: () => void;
  onCreate?: (data: RezeptLimitFormData) => Promise<void>;
  isCreating?: boolean;
  onEdit?: (id: number, data: RezeptLimitFormData) => Promise<void>;
  isEditing?: boolean;
  onFetchDetail?: (id: number) => Promise<RezeptLimit | null>;
  onBulkCreate?: (year: number) => Promise<{ created_count: number; skipped_count: number; already_exists: boolean }>;
  isBulkCreating?: boolean;
}

const AdminRezeptLimitListTemplate = ({
  data,
  current,
  total,
  pageSize,
  sortInfo,
  isLoading,
  onChange,
  onTableChange,
  breadcrumbItems,
  searchYear = '',
  onSearchYear,
  onSearch,
  onCreate,
  isCreating = false,
  onEdit,
  isEditing = false,
  onFetchDetail,
  onBulkCreate,
  isBulkCreating = false,
}: AdminRezeptLimitListTemplateProps) => {
  const { colors } = useAdminTheme();
  const modal = useAppModal();
  const toast = useAppToast();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false);
  const [bulkCreateYear, setBulkCreateYear] = useState<string>(() => {
    return convertWareki(new Date().getFullYear()) + '年度';
  });
  const [editingRecord, setEditingRecord] = useState<RezeptLimit | null>(null);
  // const [isFetchingDetail, setIsFetchingDetail] = useState(false);

  const btnPrimaryStyle = {
    backgroundColor: colors.primary,
    color: '#fff',
    borderColor: colors.primary,
  };
  const btnSearchStyle = {
    backgroundColor: colors.secondary,
    color: '#fff',
    borderColor: colors.secondary,
  };

  const handleCreate = async (data: RezeptLimitFormData) => {
    await onCreate?.(data);
    setIsCreateModalOpen(false);
  };

  const handleBulkCreateSubmit = () => {
    if (!bulkCreateYear) return;

    const westernYearStr = convertSeirekiWrap(bulkCreateYear.replace('年度', '年'));
    const westernYear = parseInt(westernYearStr, 10);

    if (Number.isNaN(westernYear)) {
      toast.error('有効な年度を選択してください。');
      return;
    }

    modal.confirm({
      title: '翌年度作成',
      content: `${bulkCreateYear}分の提出年月を作成します。よろしいですか？`,
      okText: 'はい',
      cancelText: 'いいえ',
      onOk: async () => {
        try {
          const res = await onBulkCreate?.(westernYear);
          if (res) {
            if (res.already_exists) {
              toast.info(`${bulkCreateYear}分の提出年月は既に作成済みです。`);
            } else if (res.skipped_count > 0) {
              toast.success(`${bulkCreateYear}分の提出年月を作成しました。作成件数：${res.created_count}件 既存のためスキップ：${res.skipped_count}件`);
            } else {
              toast.success(`${bulkCreateYear}分の提出年月を作成しました。`);
            }
            setIsBulkCreateModalOpen(false);
          }
        } catch (err) {
          console.error(err);
        }
      },
    });
  };

  const handleEditClick = useCallback(
    async (record: RezeptLimit) => {
      if (!onFetchDetail) {
        setEditingRecord(record);
        return;
      }
      // setIsFetchingDetail(true);
      const detail = await onFetchDetail(record.id);
      // setIsFetchingDetail(false);
      if (detail) {
        setEditingRecord(detail);
      }
    },
    [onFetchDetail]
  );

  const handleEdit = async (data: RezeptLimitFormData) => {
    if (!editingRecord) return;
    await onEdit?.(editingRecord.id, data);
    setEditingRecord(null);
  };

  // Convert RezeptLimit record to form initial values
  const editInitialValues = editingRecord
    ? {
      rezept_yearmonth: `${String(editingRecord.rezept_yearmonth).slice(0, 4)}-${String(editingRecord.rezept_yearmonth).slice(4, 6)}`,
      limit_date: editingRecord.limit_date,
    }
    : undefined;

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} />
      <h3 className="text-xl">提出期限</h3>

      {/* Filter */}
      <div className="bg-white rounded-md border p-4 mb-4">
        <div className="grid grid-cols-4 gap-4 items-end">
          <div className="flex flex-col">
            <AppAdminFormLabel label="年" />
            <div className="mt-1">
              <RA2NDatePicker
                picker="year"
                className="w-full"
                placeholder="年"
                value={searchYear}
                onChange={value => {
                  onSearchYear?.(getYearString(value));
                }}
                onEnter={onSearch}
              />
            </div>
          </div>

          <div>
            <AppButton
              onClick={onSearch}
              icon={<Search />}
              style={btnSearchStyle}
              className="py-1 px-4 text-white btn-ra2-hover font-medium h-[40px]"
            >
              検索
            </AppButton>
          </div>
        </div>
      </div>

      {/* 新規 / 翌年度を作成 buttons */}
      {(onCreate || onBulkCreate) && (
        <div className="flex gap-2 my-1">
          {onCreate && (
            <AppButton
              icon={<Plus />}
              className="py-1"
              style={btnPrimaryStyle}
              onClick={() => setIsCreateModalOpen(true)}
            >
              新規
            </AppButton>
          )}
          {onBulkCreate && (
            <AppButton
              icon={<Plus />}
              className="py-1"
              style={btnPrimaryStyle}
              onClick={() => setIsBulkCreateModalOpen(true)}
            >
              翌年度を作成
            </AppButton>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-md border">
        <RezeptLimitTable
          data={data}
          sortInfo={sortInfo}
          isLoading={isLoading}
          onTableChange={onTableChange}
          onEdit={handleEditClick}
        />
        <div className="p-4">
          <AppPagination
            current={current}
            total={total}
            pageSize={pageSize}
            onChange={onChange}
            hideOnSinglePage
          />
        </div>
      </div>
      {/* Create Modal */}
      <AppModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="提出期限作成"
        maxWidth="max-w-md"
        footer={null}
      >
        <RezeptLimitForm onSubmit={handleCreate} isLoading={isCreating} />
      </AppModal>

      {/* Edit Modal */}
      <AppModal
        isOpen={!!editingRecord}
        onClose={() => setEditingRecord(null)}
        title={`提出期限編集:${editingRecord?.id ?? ''}`}
        maxWidth="max-w-md"
        footer={null}
      >
        {editingRecord && (
          <RezeptLimitForm
            key={editingRecord.id}
            initialValues={editInitialValues}
            onSubmit={handleEdit}
            isLoading={isEditing}
          />
        )}
      </AppModal>

      {/* Bulk Create Modal */}
      <AppModal
        isOpen={isBulkCreateModalOpen}
        onClose={() => setIsBulkCreateModalOpen(false)}
        title="翌年度作成"
        maxWidth="max-w-md"
        footer={null}
      >
        <div className="space-y-4">
          <div>
            <AppAdminFormLabel label="年度" />
            <div className="mt-1">
              <RA2NDatePicker
                picker="year"
                yearFormat="yyyy年度"
                className="w-full"
                placeholder="年度"
                value={bulkCreateYear}
                onChange={setBulkCreateYear}
                startDate="1868-01-01"
                endDate="2057-12-31"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <AppButton
              onClick={() => setIsBulkCreateModalOpen(false)}
              className="py-1 px-4 btn-ra2-hover"
            >
              キャンセル
            </AppButton>
            <AppButton
              onClick={handleBulkCreateSubmit}
              style={btnPrimaryStyle}
              className="py-1 px-4 btn-ra2-hover text-white font-medium"
              loading={isBulkCreating}
            >
              作成
            </AppButton>
          </div>
        </div>
      </AppModal>
    </AppContainer>
  );
};

export default AdminRezeptLimitListTemplate;
